import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Hammer, MapPin, Filter } from "lucide-react";

const CATS = ["elettricista", "idraulico", "frigorista"];
const STATUSES = ["da_fare", "in_corso", "sospeso", "chiuso", "annullato"];
const STATUS_STYLES = {
  da_fare: "bg-slate-100 text-slate-800",
  in_corso: "bg-amber-100 text-amber-900",
  sospeso: "bg-orange-100 text-orange-900",
  chiuso: "bg-emerald-100 text-emerald-900",
  annullato: "bg-red-100 text-red-900",
};

export default function Jobs() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ category: "all", status: "all", city: "all" });
  const [cities, setCities] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const isWorker = user?.role === "operaio";
  const canCreate = user?.role && user.role !== "cliente";

  useEffect(() => {
    if (!loading && (!user || user.role === "cliente")) navigate("/login");
  }, [user, loading, navigate]);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams(f).toString();
      const r = await api.get(`/jobs?${params}`);
      setJobs(r.data);
      setCities([...new Set(r.data.map(j => j.city).filter(Boolean))]);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (user && user.role !== "cliente") load(); /* eslint-disable-next-line */ }, [f, user]);

  if (!user || user.role === "cliente") return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mt-6 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Cantieri</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2"><Hammer className="w-7 h-7"/>Lavori</h1>
          </div>
          {canCreate && (
            <Button onClick={() => setCreateOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="job-new-btn">
              <Plus className="w-4 h-4 mr-2"/>Nuovo lavoro
            </Button>
          )}
        </div>

        <Card className="p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <FilterBox label="Categoria" value={f.category} onChange={v => setF({...f, category: v})}
                     options={[{value:"all",label:"Tutte"}, ...CATS.map(c=>({value:c,label:c}))]} testid="job-filter-cat"/>
          <FilterBox label="Stato" value={f.status} onChange={v => setF({...f, status: v})}
                     options={[{value:"all",label:"Tutti"}, ...STATUSES.map(s=>({value:s,label:s}))]} testid="job-filter-status"/>
          <FilterBox label="Città" value={f.city} onChange={v => setF({...f, city: v})}
                     options={[{value:"all",label:"Tutte"}, ...cities.map(c=>({value:c,label:c}))]} testid="job-filter-city"/>
        </Card>

        {busy && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div>}
        {!busy && jobs.length === 0 && <Card className="p-8 text-center text-slate-500 border-dashed">Nessun lavoro</Card>}
        <div className="space-y-3">
          {jobs.map(j => (
            <Card key={j.job_id} className="p-5 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/lavori/${j.job_id}`)}
                  data-testid={`job-row-${j.job_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[j.status] || "bg-slate-100"}`}>{j.status}</span>
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 text-xs font-medium capitalize">{j.category}</span>
                    <span className="text-xs text-slate-500 font-mono">{j.job_id}</span>
                  </div>
                  <h3 className="heading font-bold text-lg text-slate-900 truncate">{j.title}</h3>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                    <span><MapPin className="w-3 h-3 inline mr-1"/>{j.city}</span>
                    {j.client_name && <span>· {j.client_name}</span>}
                    {j.scheduled_date && <span className="text-blue-800 font-semibold">📅 {new Date(j.scheduled_date).toLocaleDateString("it-IT")}</span>}
                    <span>· creato {new Date(j.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <JobDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load}/>
    </div>
  );
}

function FilterBox({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1 block flex items-center gap-1"><Filter className="w-3 h-3"/>{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue/></SelectTrigger>
        <SelectContent>{options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function JobDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ title: "", category: "elettricista", city: "", address: "", client_name: "", client_contact: "", description: "", scheduled_date: "" });
  const [busy, setBusy] = useState(false);
  const [operai, setOperai] = useState([]);
  const [assigned, setAssigned] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/operai").then(r => setOperai(r.data)).catch(()=>{});
      setForm({ title: "", category: "elettricista", city: "", address: "", client_name: "", client_contact: "", description: "", scheduled_date: "" });
      setAssigned([]);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/jobs", { ...form, assigned_operai: assigned });
      toast.success("Lavoro creato");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuovo lavoro</DialogTitle>
          <DialogDescription>Crea un cantiere / intervento da assegnare</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="job-create-form">
          <Input required placeholder="Titolo" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="job-title"/>
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.category} onValueChange={v=>setForm({...form,category:v})}>
              <SelectTrigger data-testid="job-cat"><SelectValue/></SelectTrigger>
              <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input required placeholder="Città" value={form.city} onChange={e=>setForm({...form,city:e.target.value})} data-testid="job-city"/>
          </div>
          <Input placeholder="Indirizzo" value={form.address} onChange={e=>setForm({...form,address:e.target.value})}/>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Data lavoro programmata</label>
              <Input type="date" value={form.scheduled_date} onChange={e=>setForm({...form,scheduled_date:e.target.value})} data-testid="job-scheduled-date"/>
            </div>
          </div>
          <Input placeholder="Nome cliente" value={form.client_name} onChange={e=>setForm({...form,client_name:e.target.value})}/>
          <Input placeholder="Telefono / Email cliente" value={form.client_contact} onChange={e=>setForm({...form,client_contact:e.target.value})}/>
          <textarea className="w-full min-h-[100px] rounded-md border border-slate-300 p-3 text-sm" placeholder="Descrizione" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/>
          {operai.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Assegna operai</div>
              <div className="space-y-1 max-h-40 overflow-y-auto border rounded p-2 bg-slate-50">
                {operai.map(o => (
                  <label key={o.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={assigned.includes(o.user_id)} onChange={e => {
                      if (e.target.checked) setAssigned([...assigned, o.user_id]);
                      else setAssigned(assigned.filter(x => x !== o.user_id));
                    }}/>
                    <span>{o.name} <span className="text-xs text-slate-500">({o.worker_category} · {o.worker_subcategory}{o.is_responsible ? " · resp" : ""})</span></span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <Button disabled={busy} className="w-full bg-blue-800" data-testid="job-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Crea lavoro"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
